/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// I need this not because we violate accessability but because we strip away the
// essential accessibility attributes when we extract the signature html and
// and this complains

// types and consts
import type { EmailSignatureV2 } from '@components/EmailSignaturev2/types';
// utils
import Emitter from '@modules/event-emitter';
import { useEffect, useState } from 'react';

import EmailSignatureBanner, { EmailSignatureDummyBanner } from '../addons/EmailSignatureBanner';
// components
import EmailSignatureEnvironmentalFooter, {
  EmailSignatureDummyEnvironmentalFooter,
} from '../addons/EmailSignatureEnvironmentalFooter';
import EmailSignatureLegalDisclaimer, {
  EmailSignatureDummyLegalDisclaimer,
} from '../addons/EmailSignatureLegalDisclaimer';
import EmailSignatureCompactMainSection from './EmailSignatureCompactMainSection';

type EmailSignatureCompactLayoutProps = {
  emailSignature: EmailSignatureV2;
  disableAllSpecialEffects: boolean;
  disableAnchorLink: boolean;
  /** Set this to true when you are copying the email signature for use in an email client.
   * Set to false when using this component in the dashboard.
   */
  isForEmailClient?: boolean;
};

const EmailSignatureCompactLayout = ({
  ref: forwardedRef,
  disableAnchorLink,
  disableAllSpecialEffects,
  emailSignature,
  isForEmailClient,
}: EmailSignatureCompactLayoutProps & {
  ref?: React.Ref<HTMLTableElement>;
}) => {
  const [showDummyEnvironmentalFooter, setShowDummyEnvironmentalFooter] = useState<boolean>(false);
  const [showDummyLegalDisclaimer, setShowDummyLegalDisclaimer] = useState<boolean>(false);
  const [showDummyBanner, setShowDummyBanner] = useState<boolean>(false);

  useEffect(() => {
    function handleShowDummyEnvironmentalFooter() {
      setShowDummyEnvironmentalFooter(true);
    }
    Emitter.on('SHOW_DUMMY_ENVIRONMENTAL_FOOTER', handleShowDummyEnvironmentalFooter);

    function removeDummyEnvironmentalFooter() {
      setShowDummyEnvironmentalFooter(false);
    }
    Emitter.on('REMOVE_DUMMY_ENVIRONMENTAL_FOOTER', removeDummyEnvironmentalFooter);

    function handleShowDummyLegalDisclaimer() {
      setShowDummyLegalDisclaimer(true);
    }
    Emitter.on('SHOW_DUMMY_LEGAL_DISCLAIMER', handleShowDummyLegalDisclaimer);

    function removeDummyLegalDisclaimer() {
      setShowDummyLegalDisclaimer(false);
    }
    Emitter.on('REMOVE_DUMMY_LEGAL_DISCLAIMER', removeDummyLegalDisclaimer);

    function handleShowDummyBanner() {
      setShowDummyBanner(true);
    }
    Emitter.on('SHOW_DUMMY_BANNER', handleShowDummyBanner);

    function removeDummyBanner() {
      setShowDummyBanner(false);
    }
    Emitter.on('REMOVE_DUMMY_BANNER', removeDummyBanner);

    return () => {
      Emitter.off('SHOW_DUMMY_ENVIRONMENTAL_FOOTER', handleShowDummyEnvironmentalFooter);
      Emitter.off('REMOVE_DUMMY_ENVIRONMENTAL_FOOTER', removeDummyEnvironmentalFooter);
      Emitter.off('SHOW_DUMMY_LEGAL_DISCLAIMER', handleShowDummyLegalDisclaimer);
      Emitter.off('REMOVE_DUMMY_LEGAL_DISCLAIMER', removeDummyLegalDisclaimer);
      Emitter.off('SHOW_DUMMY_BANNER', handleShowDummyBanner);
      Emitter.off('REMOVE_DUMMY_BANNER', removeDummyBanner);
    };
  }, []);

  return (
    <table
      ref={forwardedRef}
      style={{
        borderCollapse: 'collapse',
        '--email-signature-theme': emailSignature.design.themeColor,
      }}
      cellSpacing="0"
      cellPadding="0"
    >
      <tbody>
        <tr>
          <td>
            <EmailSignatureCompactMainSection
              emailSignature={emailSignature}
              disableAllSpecialEffects={disableAllSpecialEffects}
              disableAnchorLink={disableAnchorLink}
              isForEmailClient={isForEmailClient}
            />
          </td>
        </tr>

        {emailSignature.banner?.imageUrl && (
          <EmailSignatureBanner
            disableAllSpecialEffects={disableAllSpecialEffects}
            emailSignature={emailSignature}
            isInEditingMode={true}
          />
        )}
        <EmailSignatureDummyBanner emailSignature={emailSignature} show={showDummyBanner} />

        {emailSignature.environmentalFooter && (
          <EmailSignatureEnvironmentalFooter
            disableAllSpecialEffects={disableAllSpecialEffects}
            emailSignature={emailSignature}
            showDummyBanner={showDummyBanner}
            isInEditingMode={true}
          />
        )}
        <EmailSignatureDummyEnvironmentalFooter
          show={showDummyEnvironmentalFooter}
          emailSignature={emailSignature}
        />

        {emailSignature.legalDisclaimer && (
          <EmailSignatureLegalDisclaimer
            disableAllSpecialEffects={disableAllSpecialEffects}
            emailSignature={emailSignature}
            showDummyBanner={showDummyBanner}
            showDummyEnvironmentalFooter={showDummyEnvironmentalFooter}
          />
        )}
        <EmailSignatureDummyLegalDisclaimer emailSignature={emailSignature} show={showDummyLegalDisclaimer} />
      </tbody>
    </table>
  );
};

EmailSignatureCompactLayout.displayName = 'EmailSignatureCompactLayout';

export default EmailSignatureCompactLayout;
